/**
 * Credentials that travel in a URL query never reach analytics or error
 * reports (2026-10-10 review). Today: the `token` of the one-tap security
 * links ("Not you? Turn it off" / "Not you? Remove it" — apps/app
 * routes/auth/2fa-revoke.tsx, remove-sign-in-method.tsx), which act without a
 * session. Those pages drop it from the address bar on mount; this covers what
 * was captured before that (a page view, a navigation breadcrumb, a session
 * recording's page `href` or network line).
 *
 * No dependencies: apps/app's eager Sentry init imports it without pulling the
 * deferred PostHog bundle in.
 */

const SECRET_QUERY_PARAMS = /([?&#;](?:token)=)[^&#\s"'<>]*/gi;

/** `value` with the value of every secret query parameter replaced. */
export function redactUrlSecrets(value: string): string {
  return value.replace(SECRET_QUERY_PARAMS, "$1[redacted]");
}

/**
 * How deep redactUrlSecretsIn walks: a PostHog `$snapshot` keeps a page's
 * `href` at properties.$snapshot_data[i].data.href (depth 4); a Sentry
 * breadcrumb's `from` / `to` sit at depth 1.
 */
const MAX_DEPTH = 6;

/**
 * Every string that may hold a URL in `value` — its properties and array
 * items, nested up to MAX_DEPTH levels — redacted in place.
 */
export function redactUrlSecretsIn(value: unknown, depth = 0): void {
  if (!value || typeof value !== "object" || depth > MAX_DEPTH) return;
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      const item: unknown = value[i];
      if (typeof item === "string") {
        if (item.includes("=")) value[i] = redactUrlSecrets(item);
      } else {
        redactUrlSecretsIn(item, depth + 1);
      }
    }
    return;
  }
  const record = value as Record<string, unknown>;
  for (const [key, item] of Object.entries(record)) {
    if (typeof item === "string") {
      if (item.includes("=")) record[key] = redactUrlSecrets(item);
    } else {
      redactUrlSecretsIn(item, depth + 1);
    }
  }
}
